/**
 * Allocations University content registry: the single source of truth for the manual.
 * Departments → pages → sections. The sidebar, routes, Departments card, library stats,
 * reading paths, page TOC and previous/next links all derive from this file, so adding a
 * section's `blocks` here updates every count, reading time and progress bar automatically.
 */
import type { LucideIcon } from "lucide-react";
import { Briefcase, Code2, Landmark, Layers, LifeBuoy, ShieldCheck } from "lucide-react";
import { DOC_SECTIONS } from "./docs";
import { DEALS_SECTIONS } from "./pages/deals";

/**
 * Structured content blocks; every block's text counts toward word totals.
 * In any text, wrap code names in backticks (`deals.status`) to render them as code.
 */
export type UniversityBlock =
  | { type: "paragraph"; text: string }
  | { type: "heading"; text: string }
  | { type: "list"; items: string[] }
  | { type: "terms"; items: { term: string; description: string }[] }
  | { type: "flow"; steps: string[] }
  | { type: "table"; columns: string[]; rows: string[][] }
  | { type: "callout"; tone: "info" | "warning"; title: string; text: string }
  /** A whole section written in Markdown (docs/*.md); `assistant` notes go to Atlas only. */
  | { type: "markdown"; text: string; assistant?: string };

/** `group` (optional) nests the section under a heading, making the page a tree in the sidebar and TOC. */
export type UniversitySection = {
  id: string;
  title: string;
  blocks: UniversityBlock[];
  group?: string;
};

export type UniversitySectionGroup = { id: string; title: string; sections: UniversitySection[] };

export type UniversityPage = {
  slug: string;
  title: string;
  description: string;
  sections: UniversitySection[];
};

export type UniversityDepartment = {
  key: "engineering" | "support" | "sales" | "operations" | "finance" | "compliance";
  label: string;
  description: string;
  icon: LucideIcon;
  pages: UniversityPage[];
};

export type ReadingPath = { audience: string; title: string; pageSlugs: string[] };

const DEALS_FOUNDATIONS: UniversitySection[] = [
  {
    id: "what-is-a-deal",
    title: "What is a deal",
    blocks: [
      {
        type: "paragraph",
        text: "A startup raising a round rarely wants dozens of small cheques on its cap table, and most individual investors can't write a cheque big enough to get in alone. A deal solves both problems. Investors pool their money into one vehicle, the vehicle makes a single investment, and it holds the result on everyone's behalf. Each investor owns a slice of the vehicle in proportion to what they put in, and gets the same slice of whatever the investment returns.",
      },
      {
        type: "paragraph",
        text: "There are two shapes. An SPV (special purpose vehicle) is formed for one investment, so investors know exactly what they are buying. A fund invests in several assets over time, so investors back the manager's strategy and their return is the result of the whole portfolio.",
      },
      {
        type: "heading",
        text: "A worked example",
      },
      {
        type: "flow",
        steps: [
          "A fund manager secures a $500,000 allocation in a startup's Series A.",
          "20 investors commit $25,000 each to the manager's SPV.",
          "The SPV wires $500,000 to the company and appears once on its cap table.",
          "Each investor owns 5% of the SPV, so 5% of whatever the shares later return, after fees and carry.",
        ],
      },
      {
        type: "terms",
        items: [
          {
            term: "Deal",
            description:
              "One raise: an SPV or a fund (`deals.type` is `spv`, `fund` or `migration`) that pools investor money into an investment. Its row holds the status, the fund manager (`fm_identity_id`) and the advisor (`advisor_id`). Economically, it is the unit everything is counted in: how much was raised, what was paid in fees and what came back.",
          },
          {
            term: "Entity",
            description:
              "The legal vehicle the deal invests through, such as the LLC or LP series (`entity_id`). Investors own units in the entity and the entity owns the asset. Because the entity is a separate legal person, each investor's risk is limited to what they put in.",
          },
          {
            term: "Organization",
            description:
              "The fund manager's account that owns the deal and its entities (`organization_id`). It holds no money itself; it groups a manager's deals so their track record can be read together.",
          },
          {
            term: "Asset",
            description:
              "What the deal buys. Each asset row points back to a deal and has its own status: `active`, `realized` or `written_off`. In money terms: `active` is still held and only worth an estimate, `realized` has been sold or exited so cash came back, and `written_off` is considered worth nothing.",
          },
        ],
      },
      {
        type: "callout",
        tone: "info",
        title: "In short",
        text: "An organization runs deals, a deal invests through an entity, and an asset is what it holds.",
      },
    ],
  },
  {
    id: "deal-lifecycle",
    title: "Deal lifecycle & statuses",
    blocks: [
      {
        type: "paragraph",
        text: "A deal's status follows the money. First the terms are written, then investors are let in to sign and wire, then the money collected is released to the asset in one or more closes. Each step exists because money should only move once the previous one is safe: no investor should wire before the documents and bank account are ready, and no money should leave before every wire is reconciled and fees are taken.",
      },
      {
        type: "paragraph",
        text: "`deals.status` moves along the main path, from draft to closed:",
      },
      {
        type: "flow",
        steps: ["draft", "submitted", "onboarding", "closing", "close-requested", "closed"],
      },
      { type: "heading", text: "Statuses" },
      {
        type: "table",
        columns: ["Status", "Meaning", "Where the money is", "Owner"],
        rows: [
          [
            "Draft",
            "The fund manager is still building the deal in Hercules. Only drafts can be archived straight away.",
            "Nowhere yet: no investor can commit.",
            "Fund manager",
          ],
          [
            "Submitted",
            "Sent to Allocations for review; ops gets the deal-submitted email and Slack alert.",
            "Still none. Legal documents and the bank account are being prepared.",
            "Operations",
          ],
          [
            "Onboarding",
            "Approved and open to investors (the submitted-to-onboarding email goes out).",
            "Investors sign and wire into the deal's own bank account. The money waits there.",
            "Operations",
          ],
          [
            "Closing",
            "Closes are under way. The first close moves the deal to `closing`.",
            "Money is released to the asset, minus fees. Investors can still come in for a later close.",
            "Operations",
          ],
          [
            "Close requested",
            "Closes are under way.",
            "As in closing: money received is on its way to the asset.",
            "Operations",
          ],
          [
            "Closed",
            "The raise is done.",
            "The money is invested. From here, value only changes with the asset, until an exit pays investors back.",
            "Operations",
          ],
        ],
      },
      {
        type: "paragraph",
        text: "Example: a $500,000 SPV opens on March 1 (onboarding). By March 20, $300,000 has arrived and the company wants it now, so a first close sends that money (minus fees) and the deal moves to `closing`. The remaining $200,000 comes in a later close, after which the deal is closed.",
      },
      { type: "paragraph", text: "Off the main path:" },
      {
        type: "list",
        items: [
          "`cancelled`, `terminated` and `archived` retire a deal; `cancelled` and `archived` hide it from anyone who does not manage it.",
          "`test` marks internal deals.",
          "Only `onboarding`, `closing`, `close-requested` and `test` deals take new investments. In other words, money can only come in while the deal is open or still closing.",
        ],
      },
      {
        type: "callout",
        tone: "warning",
        title: "Differs from outline",
        text: "There is no `shutdown` status; winding down is handled outside `deals.status`. `active` is a legacy value Hercules no longer writes.",
      },
    ],
  },
  {
    id: "roles-on-a-deal",
    title: "Roles on a deal",
    blocks: [
      {
        type: "paragraph",
        text: "Every deal brings together people who want different things. Investors provide the money and want a return. The fund manager provides access to the deal and their judgement, and wants to be paid for it. Allocations provides the legal, banking and admin work that makes the vehicle real. Knowing who earns what explains most of the rules on a deal.",
      },
      {
        type: "terms",
        items: [
          {
            term: "Fund manager",
            description:
              "The identity in `deals.fm_identity_id`. Creates the deal, invites investors and requests closes. They earn the management fee (a percentage of investors' capital, paid whatever happens) and the carry (a share of the profits, paid only if the deal makes money).",
          },
          {
            term: "Advisor",
            description:
              "The identity in `deals.advisor_id`, the registered advisor on the deal. Managing other people's money for a fee usually needs a registered or exempt reporting adviser, and this is the one on record.",
          },
          {
            term: "Investor",
            description:
              "Anyone with an investment in the deal, with their own subscription documents. They bear the risk and keep the return, after fees and carry. Their documents fix how much they put in and on what terms.",
          },
          {
            term: "Allocations ops",
            description:
              "Admins in Poseidon who review submitted deals, move them to onboarding, run closes and approve requests. Allocations is paid for setting up and running the vehicle; it does not share in the profits.",
          },
        ],
      },
      {
        type: "heading",
        text: "Who earns what: an example",
      },
      {
        type: "table",
        columns: ["Step", "Investors", "Fund manager"],
        rows: [
          ["Investors commit", "$500,000", "—"],
          ["2% one-time management fee, added on top", "Wire $510,000", "$10,000"],
          ["Exit: the shares sell for $1,500,000", "Profit is $1,000,000", "—"],
          ["20% carry on the profit", "Keep $800,000 of profit", "$200,000"],
          ["Total", "$1,300,000 back on $510,000 wired", "$210,000"],
        ],
      },
      {
        type: "paragraph",
        text: "Investors trust the deal to the people in the fund manager and advisor seats, so those seats can't change on a whim. Fund manager and advisor are the two deal roles in code (`DealRole`). Changing either goes through a role-change request:",
      },
      { type: "flow", steps: ["pending", "approved", "applied"] },
      {
        type: "list",
        items: [
          "A request can also end `rejected` or `cancelled`.",
          "`awaiting_action` means the date has arrived but KYC/KYB is still needed: a new fund manager must be identity-checked before they can control investors' money.",
          "Inside an organization, members are `admin` or `fund-manager`.",
        ],
      },
    ],
  },
  {
    id: "hercules-vs-poseidon",
    title: "Hercules vs Poseidon",
    blocks: [
      {
        type: "paragraph",
        text: "The split between the two apps mirrors a basic rule of handling other people's money: the person raising it should not be the one who moves it. The fund manager builds the deal and brings investors in Hercules. Allocations, as administrator, checks the deal, runs the closes and the banking in Poseidon. Investors are protected because no single party controls both the terms and the cash.",
      },
      {
        type: "table",
        columns: ["Area", "Hercules", "Poseidon"],
        rows: [
          ["Who uses it", "Fund managers", "Allocations ops"],
          ["Repos", "`hercules_ui` + `hercules_api`", "`poseidon_ui` + `poseidon_api`"],
          [
            "Deal setup",
            "Creates and edits drafts, submits the deal",
            "Moves deals from `submitted` to `onboarding`, sends the emails",
          ],
          ["Investors and closes", "Invites investors, requests closes", "Runs closes and banking"],
          [
            "Changes",
            "Asks for changes such as role changes or archiving",
            "Approves role-change and archive requests",
          ],
          ["Reporting", "—", "Watches the dashboard numbers"],
        ],
      },
      {
        type: "callout",
        tone: "info",
        title: "One source of truth",
        text: "Both apps read and write the same `deals` table, so a status change in one shows up in the other.",
      },
    ],
  },
];

/** Reading speed used for every "minutes" figure. */
export const WORDS_PER_MINUTE = 200;

/** Departments under this word count are flagged "Thin" / "Needs work". */
export const THIN_WORD_THRESHOLD = 150;

const page = (slug: string, title: string, description: string): UniversityPage => ({
  slug,
  title,
  description,
  sections: DOC_SECTIONS[slug] ?? [],
});

export const UNIVERSITY_DEPARTMENTS: UniversityDepartment[] = [
  {
    key: "engineering",
    label: "Engineering",
    description: "Our apps, cloud, code and stack.",
    icon: Code2,
    pages: [page("engineering", "Engineering", "Our apps, cloud, code and stack.")],
  },
  {
    key: "support",
    label: "Support",
    description: "How we handle client support in Athena, with a 1 hour response target.",
    icon: LifeBuoy,
    pages: [page("support", "Support", "How we handle client support in Athena.")],
  },
  {
    key: "sales",
    label: "Sales",
    description: "Booking calls, client archetypes, and our products.",
    icon: Briefcase,
    pages: [page("sales", "Sales", "Booking calls, client archetypes and our products.")],
  },
  {
    key: "operations",
    label: "Operations",
    description: "Deals, Identities, Fund Managers, Entities and more.",
    icon: Layers,
    pages: [
      {
        ...page("deals", "Deals", "How a deal is set up and run."),
        sections: [
          ...DEALS_FOUNDATIONS.map((s) => ({ ...s, group: "Foundations" })),
          ...DEALS_SECTIONS,
        ],
      },
      page(
        "deals-handbook",
        "Deals handbook",
        "Every step of a deal in depth, from setup to exit.",
      ),
      page("identities", "Identities", "Investor identities, KYC and accreditation."),
      page("fund-managers", "Fund Managers", "Working with fund managers."),
      page("entities", "Entities", "The legal entities behind each fund."),
      page("legal-documents", "Legal Documents", "Generating and signing fund documents."),
      page("banking", "Banking", "Bank accounts, wires and reconciliation."),
      page("valuations", "Valuations", "Asset valuations and marks."),
      page("membership-transfers", "Membership Transfers", "Moving interests between investors."),
      page("distributions", "Distributions", "Paying out proceeds to investors."),
      page("taxes", "Taxes", "K-1s and tax filings."),
    ],
  },
  {
    key: "finance",
    label: "Finance",
    description: "Xero, Poseidon invoices, reconciliation and month-end close.",
    icon: Landmark,
    pages: [page("finance", "Finance", "Xero, invoices, reconciliation and month-end close.")],
  },
  {
    key: "compliance",
    label: "Compliance",
    description: "KYC/AML policy, reviews and regulatory filings.",
    icon: ShieldCheck,
    pages: [page("compliance", "Compliance", "KYC/AML policy, reviews and regulatory filings.")],
  },
];

export const READING_PATHS: ReadingPath[] = [
  {
    audience: "Operations",
    title: "A fund, end to end",
    pageSlugs: ["deals", "entities", "identities", "banking", "distributions", "taxes"],
  },
  {
    audience: "Sales + Support",
    title: "Working with fund managers",
    pageSlugs: ["sales", "fund-managers", "support"],
  },
  { audience: "Engineering", title: "Your first week in Engineering", pageSlugs: ["engineering"] },
];

// ---- Derived values (never hardcode these) ----

export const countWords = (text: string): number => text.split(/\s+/).filter(Boolean).length;

export const readingMinutes = (words: number): number => Math.ceil(words / WORDS_PER_MINUTE);

/** Markdown reduced to its words: syntax marks and link targets dropped. */
const markdownText = (md: string): string =>
  md
    .replaceAll(/\]\([^)]*\)/g, "]")
    .replaceAll(/[#*_>|[\]`]/g, " ")
    .replaceAll(/-{3,}/g, " ");

/** Plain text of one block, used for word counts and reading times. */
export const blockText = (b: UniversityBlock): string => {
  switch (b.type) {
    case "paragraph":
    case "heading":
      return b.text;
    case "list":
      return b.items.join(" ");
    case "terms":
      return b.items.map((i) => `${i.term} ${i.description}`).join(" ");
    case "flow":
      return b.steps.join(" ");
    case "table":
      return [...b.columns, ...b.rows.flat()].join(" ");
    case "callout":
      return `${b.title} ${b.text}`;
    case "markdown":
      return markdownText(b.text);
  }
};

/** A section's editable text: one block per paragraph, the unit a suggested edit is made against. */
/** A section written as Markdown (docs/*.md), whose passages may be hard-wrapped. */
export const isMarkdownSection = (s: UniversitySection): boolean =>
  s.blocks.length > 0 && s.blocks.every((b) => b.type === "markdown");

export const sectionPassage = (s: UniversitySection): string =>
  s.blocks.map(blockPassage).join("\n\n");

/**
 * One block's editable text, the unit a selection-based edit is made against. Like blockText,
 * but keeps structure readable: one row, item or step per line.
 */
export const blockPassage = (b: UniversityBlock): string => {
  switch (b.type) {
    case "list":
      return b.items.map((i) => `- ${i}`).join("\n");
    case "terms":
      return b.items.map((i) => `${i.term}: ${i.description}`).join("\n");
    case "flow":
      return b.steps.map((step, i) => `${i + 1}. ${step}`).join("\n");
    case "table":
      return [b.columns, ...b.rows].map((row) => row.join(" | ")).join("\n");
    case "callout":
      return `${b.title}\n${b.text}`;
    case "markdown":
      return b.text;
    default:
      return blockText(b);
  }
};

const sectionText = (s: UniversitySection): string =>
  [s.title, ...s.blocks.map(blockText)].join(" ").replaceAll("`", "");

export const pageWords = (p: UniversityPage): number =>
  p.sections.reduce((sum, s) => sum + countWords(sectionText(s)), 0);

export const departmentWords = (d: UniversityDepartment): number =>
  d.pages.reduce((sum, p) => sum + pageWords(p), 0);

export const isThin = (d: UniversityDepartment): boolean =>
  departmentWords(d) < THIN_WORD_THRESHOLD;

/** Every page in registry order, with its department; drives routes and previous/next. */
export const UNIVERSITY_PAGES = UNIVERSITY_DEPARTMENTS.flatMap((department) =>
  department.pages.map((p) => ({ ...p, department })),
);

export type UniversityPageEntry = (typeof UNIVERSITY_PAGES)[number];

export const findPage = (slug: string): UniversityPageEntry | undefined =>
  UNIVERSITY_PAGES.find((p) => p.slug === slug);

export const adjacentPages = (slug: string) => {
  const i = UNIVERSITY_PAGES.findIndex((p) => p.slug === slug);
  return { previous: UNIVERSITY_PAGES[i - 1], next: i < 0 ? undefined : UNIVERSITY_PAGES[i + 1] };
};

export const UNIVERSITY_HOME = "/atlas";
export const UNIVERSITY_OVERVIEW = `${UNIVERSITY_HOME}/overview`;
export const UNIVERSITY_SUGGESTIONS = `${UNIVERSITY_HOME}/suggestions`;
/** Support Pulse: the monthly read of what customers struggled with in support conversations. */
export const UNIVERSITY_PULSE = `${UNIVERSITY_HOME}/pulse`;

/** Talk To Atlas: the Claude chat that answers from every page. It is the Atlas landing page. */
export const UNIVERSITY_ATLAS = UNIVERSITY_HOME;

/** Single-page departments live at /atlas/<department>; others at /atlas/<department>/<page>. */
export const pageUrl = (slug: string): string => {
  const entry = findPage(slug);
  if (!entry) return UNIVERSITY_OVERVIEW;
  const base = `${UNIVERSITY_HOME}/${entry.department.key}`;
  return entry.department.pages.length === 1 ? base : `${base}/${entry.slug}`;
};

/** Resolves a /atlas/<department>[/<page>] URL; a bare multi-page department resolves to its first page. */
export const findPageByPath = (
  departmentKey: string,
  pageSlug?: string,
): UniversityPageEntry | undefined => {
  const department = UNIVERSITY_DEPARTMENTS.find((d) => d.key === departmentKey);
  const target = pageSlug
    ? department?.pages.find((p) => p.slug === pageSlug)
    : department?.pages[0];
  return target && department ? { ...target, department } : undefined;
};

export const libraryStats = () => {
  const words = UNIVERSITY_PAGES.reduce((sum, p) => sum + pageWords(p), 0);
  return {
    pages: UNIVERSITY_PAGES.length,
    written: UNIVERSITY_PAGES.filter((p) => pageWords(p) > 0).length,
    sections: UNIVERSITY_PAGES.reduce((sum, p) => sum + p.sections.length, 0),
    words,
    minutes: readingMinutes(words),
  };
};

/** Consecutive sections sharing a `group`, in page order; ungrouped sections form untitled groups. */
export const sectionGroups = (sections: UniversitySection[]): UniversitySectionGroup[] => {
  const groups: UniversitySectionGroup[] = [];
  for (const section of sections) {
    const title = section.group ?? "";
    const last = groups.at(-1);
    if (last?.title === title) {
      last.sections.push(section);
    } else {
      const slug = title.toLowerCase().replaceAll(/[^a-z0-9]+/g, "-");
      groups.push({ id: `group-${slug || section.id}`, title, sections: [section] });
    }
  }
  return groups;
};

export const hasSectionGroups = (p: UniversityPage): boolean =>
  p.sections.some((s) => s.group !== undefined);

export const readingPathPages = (path: ReadingPath) =>
  path.pageSlugs.map(findPage).filter((p): p is UniversityPageEntry => p !== undefined);
